import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  FileText,
  Ruler,
  Layers,
  ScanLine,
  PoundSterling,
  ArrowRight,
} from "lucide-react";
import { SeoPageFooter } from "@/components/seo-page-footer";

const PAGE_URL = "https://thepropertool.com/ai-takeoff-software";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | AI Takeoff Software for UK Builders",
  "description": "AI takeoff software that reads your architect's drawings and structural plans, extracts quantities automatically, and prices every trade in under 10 minutes. Built for UK builders.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "AI Quoting Software", "item": "https://thepropertool.com/ai-quoting-software" },
    { "@type": "ListItem", "position": 3, "name": "AI Takeoff Software", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is AI takeoff software?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "AI takeoff software reads your construction drawings (PDFs of architect's plans, structural engineers' drawings, or site layouts) and automatically extracts the dimensions, quantities, and specifications needed to price a job. Instead of measuring drawings manually and building a spreadsheet, the AI does the takeoff and produces a priced scope of works you can review and send.",
      },
    },
    {
      "@type": "Question",
      "name": "Can AI read structural drawings?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool's AI reads PDF drawings and extracts structural details: beam section sizes (UB, UC, RHS), span dimensions, floor areas, storey heights, and connection types. It uses these to price structural steel, groundworks, and related trades accurately, based on what the drawings actually specify.",
      },
    },
    {
      "@type": "Question",
      "name": "How accurate is AI takeoff from drawings?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Accuracy depends on drawing quality and detail level. The Proper Tool improves accuracy with a purpose-built validation pipeline that cross-checks extracted quantities against the project description, flags scope gaps, and detects pricing outliers before you send the quote. Detailed GA and structural drawings consistently produce the most accurate output.",
      },
    },
    {
      "@type": "Question",
      "name": "Does the AI work with PDF drawings?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Upload your drawings as PDFs: architect's general arrangement plans, structural engineer's drawings, or engineer's reports. The AI extracts the relevant quantities and specifications. It processes multi-page PDFs and handles standard A1/A3/A4 drawing sheets.",
      },
    },
    {
      "@type": "Question",
      "name": "Is The Proper Tool free to try?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features including drawing analysis. No credit card is required to start. After the trial, plans start from £29/month for sole traders.",
      },
    },
  ],
};

const FAQS = [
  {
    q: "What is AI takeoff software?",
    a: "AI takeoff software reads your construction drawings (PDFs of architect's plans, structural engineers' drawings, or site layouts) and automatically extracts the dimensions, quantities, and specifications needed to price a job. Instead of measuring drawings manually and building a spreadsheet, the AI does the takeoff and produces a priced scope of works you can review and send.",
  },
  {
    q: "Can AI read structural drawings?",
    a: "Yes. The Proper Tool's AI reads PDF drawings and extracts structural details: beam section sizes (UB, UC, RHS), span dimensions, floor areas, storey heights, and connection types. It uses these to price structural steel, groundworks, and related trades accurately, based on what the drawings actually specify.",
  },
  {
    q: "How accurate is AI takeoff from drawings?",
    a: "Accuracy depends on drawing quality and detail level. The Proper Tool improves accuracy with a purpose-built validation pipeline that cross-checks extracted quantities against the project description, flags scope gaps, and detects pricing outliers before you send the quote. Detailed GA and structural drawings consistently produce the most accurate output.",
  },
  {
    q: "Does the AI work with PDF drawings?",
    a: "Yes. Upload your drawings as PDFs: architect's general arrangement plans, structural engineer's drawings, or engineer's reports. The AI extracts the relevant quantities and specifications. It processes multi-page PDFs and handles standard A1/A3/A4 drawing sheets.",
  },
  {
    q: "Is The Proper Tool free to try?",
    a: "Yes. The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features including drawing analysis. No credit card is required to start. After the trial, plans start from £29/month for sole traders.",
  },
];

const DRAWING_TYPES = [
  {
    icon: FileText,
    title: "Architect's GA plans",
    description: "General arrangement plans showing floor layouts, dimensions, room sizes, and overall footprint, all extracted and priced in one pass.",
  },
  {
    icon: Layers,
    title: "Structural engineer's drawings",
    description: "Beam schedules, section sizes, pad foundations, steel connections: the AI reads UB, UC, and RHS specifications directly from the drawing.",
  },
  {
    icon: Ruler,
    title: "Multi-page drawing sets",
    description: "Upload a full drawing package as a single PDF. The AI processes every page and consolidates the scope before pricing.",
  },
  {
    icon: ScanLine,
    title: "Engineer's reports",
    description: "Structural engineer's reports and specifications are read alongside drawings to cross-check beam specs and connection requirements.",
  },
];

const RELATED_PAGES = [
  { href: "/ai-quoting-software/", label: "AI Quoting Software" },
  { href: "/quote-from-photo/", label: "Quote from Photo" },
  { href: "/quote-from-drawings/", label: "Quote from Drawings" },
  { href: "/auto-quote/", label: "Auto Quote" },
  { href: "/quote-from-description/", label: "Quote from a Description" },
  { href: "/ai-construction-estimating-software/", label: "AI Construction Estimating" },
  { href: "/construction-estimating-software/", label: "Construction Estimating Software" },
  { href: "/estimating-software-for-builders/", label: "Estimating Software for Builders" },
  { href: "/quoting-software-for-builders/", label: "Quoting Software for Builders" },
  { href: "/pricing/", label: "Pricing" },
  { href: "/free-trial/", label: "Free Trial" },
];

export default function AiTakeoffSoftwarePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="AI Takeoff Software for UK Builders | The Proper Tool"
        description="AI takeoff software that reads your architect's drawings and structural plans, extracts quantities automatically, and prices every trade in under 10 minutes. Built for UK builders."
        canonical="/ai-takeoff-software"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center flex-wrap">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/ai-quoting-software" className="hover:text-foreground transition-colors">AI Quoting Software</Link>
              <ChevronRight className="h-4 w-4" />
              <span>AI Takeoff Software</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              AI Takeoff Software for UK Builders
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Upload your architect's plans or structural drawings and get a fully-priced, itemised quote in under 10 minutes. The AI reads beam sizes, floor areas, and spans, so you don't have to.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/pricing/">See pricing</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · 5 total quote creations across Auto, Template, and Manual quotes · No credit card required</p>
          </div>
        </section>

        {/* What is AI takeoff */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">What is AI takeoff software?</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              A takeoff is the process of reading a set of construction drawings and extracting every quantity needed to price the job: floor areas, wall lengths, beam sections, window counts, and more. Traditionally this takes hours and is done by hand with a scale rule or a measuring tool.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              AI takeoff software replaces that process. Upload your PDF drawings and the AI reads them: it identifies structural members, measures floor areas, picks up beam section sizes, and translates the whole drawing set into a priced scope of works, ready to review and send.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              The Proper Tool is purpose-built for UK residential construction. It knows the difference between a 178×102 UB and a 203×203 UC, applies the correct UK cost library rates to every element, and produces a professional PDF quote your client expects.
            </p>
          </div>
        </section>

        {/* Drawing types */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Works with every drawing type</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Upload the drawings you already have; the AI adapts to the level of detail provided.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {DRAWING_TYPES.map((type) => (
                <div key={type.title} className="bg-background rounded-xl border border-border p-6">
                  <type.icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-semibold text-base mb-2">{type.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{type.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What the AI extracts */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">What the AI extracts from your drawings</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              The Proper Tool doesn't just scan drawings for text: it interprets them. Every quantity it extracts feeds directly into the quote, priced at current UK construction rates.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Floor areas, ceiling heights, and overall footprint dimensions",
                "Structural beam section sizes: UB, UC, RHS, SHS, CHS",
                "Pad foundation dimensions and load-bearing wall specifications",
                "Window and door openings with lintel requirements",
                "Storey heights and loft conversion headroom",
                "Extensions: single and double storey, side return, wrap-around",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* From drawings to priced quote */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">From drawings to priced quote in three steps</h2>
            <div className="space-y-6">
              {[
                {
                  step: "1",
                  title: "Upload your drawings",
                  desc: "Upload one or more PDF drawings. Add a plain-English description of the job alongside if you have one; the AI uses both.",
                },
                {
                  step: "2",
                  title: "The AI does the takeoff",
                  desc: "The AI reads every page, extracts quantities and specifications, and maps them to the UK cost library across all 31 trade categories.",
                },
                {
                  step: "3",
                  title: "Review and send",
                  desc: "You get back a fully itemised, priced quote. Edit any line, adjust quantities, swap rates, then export as a branded PDF.",
                },
              ].map(({ step, title, desc }) => (
                <div key={step} className="flex gap-5">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                    {step}
                  </div>
                  <div>
                    <h3 className="font-semibold text-base mb-1">{title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Built on UK rates */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Priced from UK construction rates, not guesswork</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Generic AI tools estimate. The Proper Tool prices. Every quantity extracted from your drawings is matched against a 519-item UK cost library covering 31 trade categories, from groundworks and structural steel through to plastering and decoration.
            </p>
            <ul className="space-y-3">
              {[
                "519 rated tasks across 31 trade categories",
                "Low, Typical, and High rate bands for every item",
                "Override any rate with your own figures",
                "Labour, materials, and plant broken out on every line",
                "VAT toggle: UK 20% or custom rate",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <PoundSterling className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Pricing callout */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="pricing">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simple pricing, free to try</h2>
              <p className="text-lg text-muted-foreground">14-day free trial · 5 total quote creations across Auto, Template, and Manual quotes · No credit card required</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6 mb-8">
              {([
                { name: "Lite", price: "£29", desc: "For sole traders", features: ["10 AI auto-quotes/mo", "Unlimited template & manual quotes", "Professional PDF export", "Full 519-item cost library"], highlight: false },
                { name: "Starter", price: "£59", desc: "For growing businesses", features: ["25 AI auto-quotes/mo", "Everything in Lite", "Pricing Accuracy Index", "Expert validation pipeline", "Regional pricing"], highlight: true },
                { name: "Pro", price: "£99", desc: "For high-volume builders", features: ["50 AI auto-quotes/mo", "Everything in Starter", "Drawing analysis", "Learned rate adjustments"], highlight: false },
              ] as { name: string; price: string; desc: string; features: string[]; highlight: boolean }[]).map((plan) => (
                <div
                  key={plan.name}
                  className={`rounded-xl border p-6 flex flex-col ${plan.highlight ? "border-primary bg-primary/5" : "border-border bg-background"}`}
                >
                  <div className="mb-4">
                    <p className="font-bold text-lg">{plan.name}</p>
                    <p className="text-3xl font-bold mt-1">{plan.price}<span className="text-base font-normal text-muted-foreground">/mo + VAT</span></p>
                    <p className="text-sm text-muted-foreground mt-1">{plan.desc}</p>
                  </div>
                  <ul className="space-y-2 flex-1 mb-6">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant={plan.highlight ? "default" : "outline"} asChild className="w-full">
                    <Link href="/login">Start free trial</Link>
                  </Button>
                </div>
              ))}
            </div>
            <div className="text-center space-x-4 text-sm">
              <Link href="/pricing/" className="text-primary font-medium hover:underline">Full pricing details</Link>
              <span className="text-muted-foreground">·</span>
              <Link href="/free-trial/" className="text-primary font-medium hover:underline">About the free trial</Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Internal links */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related pages</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {RELATED_PAGES.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to quote from drawings?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Start your free 14-day trial. 5 total quote creations across Auto, Template, and Manual quotes included. No credit card required.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">No credit card required · Cancel any time</p>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
